import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X, Upload } from 'lucide-react';
import '@/components/ui/styles/customScrollBar.css';
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";

const PTPREditForm = ({ formData, handleInputChange, handleFileChange, removeFile }) => {
   const renderFileInputs = () => {
      const fileTypes = [
         { type: 'letterRequest', label: 'Letter Request', required: true },
         { type: 'titleOrTaxDeclaration', label: 'Title or Tax Declaration', required: true },
         { type: 'darCertification', label: 'DAR Certification', condition: formData.isCLOA },
         { type: 'specialPowerOfAttorney', label: 'Special Power of Attorney', condition: formData.isRepresentative }
      ];

      return fileTypes.map(({ type, label, required, condition }) => {
         // Skip conditional documents if their condition is not met
         if (condition !== undefined && !condition) return null;

         return (
            <div key={type} className="mb-4">
               <Label htmlFor={type} className="block mb-2 text-foreground">
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
               </Label>
               <div>
                  {formData.files && formData.files[type] && formData.files[type].length > 0 ? (
                     formData.files[type].map((file, index) => (
                        <div key={index} className="flex items-center justify-between mb-2 bg-muted/50 p-2 rounded">
                           <span className="text-sm text-muted-foreground truncate">{file.filename}</span>
                           <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(type, index)}
                           >
                              <X className="h-4 w-4" />
                           </Button>
                        </div>
                     ))
                  ) : (
                     <div className="mb-2 bg-muted/50 p-2 rounded">
                        <span className="text-sm text-muted-foreground">No uploaded file</span>
                     </div>
                  )}
                  <div className="flex items-center mt-2">
                     <Input
                        id={`${type}-${formData.files?.[type]?.length || 0}`}
                        name={type}
                        type="file"
                        onChange={(e) => handleFileChange(e, type)}
                        className="hidden"
                     />
                     <Label
                        htmlFor={`${type}-${formData.files?.[type]?.length || 0}`}
                        className="cursor-pointer flex items-center justify-center w-full px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
                     >
                        <Upload className="mr-2 h-4 w-4" />
                        {formData.files && formData.files[type] && formData.files[type].length > 0
                           ? `Add Another ${label}`
                           : `Upload ${label}`
                        }
                     </Label>
                  </div>
               </div>
            </div>
         );
      });
   };

   return (
      <div className="pl-2 space-y-4 custom-scrollbar pr-4" style={{ maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
         <div>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Personal Information</h3>
            <div className="space-y-4">
               <div>
                  <Label htmlFor="ownerName" className="text-foreground">Name of Lot Owner</Label>
                  <Input
                     id="ownerName"
                     name="ownerName"
                     value={formData.ownerName || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
               <div>
                  <Label htmlFor="address" className="text-foreground">Address</Label>
                  <Input
                     id="address"
                     name="address"
                     value={formData.address || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
               <div>
                  <Label htmlFor="contactNumber" className="text-foreground">Contact Number</Label>
                  <Input
                     id="contactNumber"
                     name="contactNumber"
                     value={formData.contactNumber || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
            </div>
         </div>

         <Separator className="my-4" />

         <div>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Plantation Details</h3>
            <div className="space-y-4">
               <div>
                  <Label htmlFor="lotArea" className="text-foreground">Lot area devoted to plantation (in hectares)</Label>
                  <Input
                     id="lotArea"
                     name="lotArea"
                     type="number"
                     step="0.01"
                     value={formData.lotArea || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
               <div>
                  <Label htmlFor="treeSpecies" className="text-foreground">Tree Species Planted</Label>
                  <Input
                     id="treeSpecies"
                     name="treeSpecies"
                     value={formData.treeSpecies ? formData.treeSpecies.join(', ') : ''}
                     onChange={(e) => handleInputChange({
                        target: {
                           name: 'treeSpecies',
                           value: e.target.value.split(',').map(s => s.trim())
                        }
                     })}
                     required
                     className="bg-background"
                     placeholder="Enter tree species (comma-separated)"
                  />
               </div>
               <div>
                  <Label htmlFor="totalTrees" className="text-foreground">Total No. of Trees Planted</Label>
                  <Input
                     id="totalTrees"
                     name="totalTrees"
                     type="number"
                     value={formData.totalTrees || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
               <div>
                  <Label htmlFor="treeSpacing" className="text-foreground">Spacing of trees</Label>
                  <Input
                     id="treeSpacing"
                     name="treeSpacing"
                     value={formData.treeSpacing || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                     placeholder="e.g., 3x3 meters"
                  />
               </div>
               <div>
                  <Label htmlFor="yearPlanted" className="text-foreground">Year Planted</Label>
                  <Input
                     id="yearPlanted"
                     name="yearPlanted"
                     type="number"
                     value={formData.yearPlanted || ''}
                     onChange={handleInputChange}
                     required
                     className="bg-background"
                  />
               </div>
            </div>
         </div>

         <Separator className="my-4" />

         <div>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Document Requirements</h3>
            <div className="space-y-4">
               <div className="flex items-center space-x-2">
                  <Checkbox
                     id="isCLOA"
                     checked={formData.isCLOA || false}
                     onCheckedChange={(checked) => handleInputChange({
                        target: {
                           name: 'isCLOA',
                           value: checked
                        }
                     })}
                  />
                  <Label htmlFor="isCLOA">
                     Is Title Certificate of Land Ownership Award (CLOA)
                  </Label>
               </div>
               <div className="flex items-center space-x-2">
                  <Checkbox
                     id="isRepresentative"
                     checked={formData.isRepresentative || false}
                     onCheckedChange={(checked) => handleInputChange({
                        target: {
                           name: 'isRepresentative',
                           value: checked
                        }
                     })}
                  />
                  <Label htmlFor="isRepresentative">
                     Is Representative
                  </Label>
               </div>
            </div>
         </div>

         <Separator className="my-4" />

         <div>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Documents</h3>
            {renderFileInputs()}
         </div>
      </div>
   );
};

export default PTPREditForm;
